import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

export interface MongoDestinationFormData {
  destinationId: string;
  connectionString: string;
  databaseName: string;
  collectionName: string;
  isEnabled: boolean;
}

interface SftpMongoDestinationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (destination: MongoDestinationFormData) => void;
  destination?: MongoDestinationFormData;
  mode: 'create' | 'edit';
}

const initialFormData: MongoDestinationFormData = {
  destinationId: '',
  connectionString: '',
  databaseName: '',
  collectionName: '',
  isEnabled: true,
};

export const SftpMongoDestinationDialog: React.FC<SftpMongoDestinationDialogProps> = ({
  open,
  onClose,
  onSave,
  destination,
  mode,
}) => {
  const [formData, setFormData] = useState<MongoDestinationFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === 'edit' && destination) {
      setFormData(destination);
    } else if (mode === 'create') {
      setFormData({
        ...initialFormData,
        destinationId: uuidv4().replace(/-/g, ''),
      });
    }
  }, [mode, destination, open]);

  const handleFieldChange = (field: keyof MongoDestinationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === 'create' && !formData.connectionString) newErrors.connectionString = 'Connection String is required';
    if (!formData.databaseName) newErrors.databaseName = 'Database Name is required';
    if (!formData.collectionName) newErrors.collectionName = 'Collection Name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Add MongoDB Destination' : 'Edit MongoDB Destination'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">MongoDB Configuration</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEnabled}
                  onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Connection String"
              value={formData.connectionString}
              onChange={(e) => handleFieldChange('connectionString', e.target.value)}
              error={!!errors.connectionString}
              helperText={errors.connectionString || (mode === 'edit' ? 'Leave blank to keep the current connection string' : undefined)}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Database Name"
              value={formData.databaseName}
              onChange={(e) => handleFieldChange('databaseName', e.target.value)}
              error={!!errors.databaseName}
              helperText={errors.databaseName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Collection Name"
              value={formData.collectionName}
              onChange={(e) => handleFieldChange('collectionName', e.target.value)}
              error={!!errors.collectionName}
              helperText={errors.collectionName}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
